import { createContext, useContext, useSyncExternalStore } from 'react';
import type { PortalClient } from './client';

/**
 * The client, for components (P0-WEB-04).
 *
 * Route loaders reach the client through `portalClient()` instead, because a
 * loader runs outside the tree and cannot call a hook. Both resolve to the same
 * instance in the running app; the context exists so a component test can
 * inject a fake without touching module state.
 */
export const ApiClientContext = createContext<PortalClient | undefined>(undefined);

export function useApiClient(): PortalClient {
  const client = useContext(ApiClientContext);

  if (client === undefined) {
    // Named rather than allowed to surface as "cannot read property request of
    // undefined" three frames deeper, where the missing provider is not
    // obviously the cause.
    throw new Error('useApiClient must be used inside <ApiProvider>.');
  }

  return client;
}

/**
 * Whether the API is currently reachable (PRD §15, "network loss mid-session").
 *
 * `useSyncExternalStore` rather than `useState` + an effect: the store is
 * mutated by requests, which can settle between render and effect, and the
 * subscription-after-render gap is exactly when a connection drops.
 */
export function useConnectivity(): boolean {
  const { connectivity } = useApiClient();

  return useSyncExternalStore(
    connectivity.subscribe,
    connectivity.getSnapshot,
    // Server snapshot. The portal does not server-render, but the landing
    // site's prerender shares components, and an undefined third argument
    // throws there rather than here.
    () => true,
  );
}
